import { AxiosError } from 'axios'
import useAxios from '../useAxios'
import { useQueue } from '../useQueue'
import { normalizeAxiosError } from '../../reducers/errorReducer'
import { QueueStatus } from '../queue/queueTypes'
import { QueueCancel } from '../queue/useQueueCancel'
import { DocumentGenerationStatusData } from './documentGenerationTypes'

/**
 * Asks the server to drop a document generation job from the Service Bus queue. Without a message id the server never
 * accepted the job, so there is nothing to delete and the job can leave the queue right away.
 */
export default function useDocumentGenerationCancel(): QueueCancel<DocumentGenerationStatusData> {
    const axios = useAxios()
    const queue = useQueue()

    return (requestId, graceful, status) => {
        if (!status?.messageId) {
            queue.remove(requestId)
            return
        }

        axios
            .delete(`document/queue/${status.messageId}`, { headers: { SuppressErrors: true } })
            .catch((response: AxiosError) => {
                // Don't report anything the server returns when we're already restarting and no longer interested in
                // status updates of the old attempt.
                if (graceful) {
                    const error = normalizeAxiosError(response)
                    queue.transition(requestId, QueueStatus.Failed, { error: { message: error?.message, trace: error?.trace } })
                }
            })
            .finally(() => graceful && queue.remove(requestId))
    }
}
